import { useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { X, ChevronLeft, ChevronRight } from "lucide-react";
import { GALLERY } from "@/data/property";
import { Reveal, MaskLineInView } from "./Reveal";

export default function Gallery() {
  const [active, setActive] = useState(null);

  const open = (i) => setActive(i);
  const close = () => setActive(null);
  const prev = () => setActive((i) => (i - 1 + GALLERY.length) % GALLERY.length);
  const next = () => setActive((i) => (i + 1) % GALLERY.length);

  return (
    <section id="gallery" data-testid="gallery-section" className="bg-[#FAFAF8] py-24 md:py-32">
      <div className="max-w-[1400px] mx-auto px-5 md:px-10">
        <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-6 mb-12">
          <div>
            <span className="text-sm uppercase tracking-widest text-[#1FB6C9] font-body">The Estate</span>
            <h2 className="mt-3 font-display font-semibold text-[#0E2A47] text-4xl md:text-6xl tracking-tight leading-none">
              <MaskLineInView>A curated tour</MaskLineInView>
            </h2>
          </div>
          <p className="max-w-md text-[#0E2A47]/70 font-body">
            From the resort-style pool to the chef's kitchen and lake-view suites — every corner was
            designed for gathering.
          </p>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-12 gap-5 md:gap-6 auto-rows-[220px] md:auto-rows-[260px]">
          {GALLERY.map((g, i) => (
            <Reveal key={g.src} delay={(i % 3) * 0.08} className={`${g.span} h-full`}>
              <button
                onClick={() => open(i)}
                data-testid={`gallery-item-${i}`}
                className="group relative h-full w-full overflow-hidden rounded-lg block"
              >
                <img
                  src={g.src}
                  alt={g.alt}
                  className="h-full w-full object-cover transition-transform duration-700 ease-out group-hover:scale-105"
                />
                <div className="absolute inset-0 bg-gradient-to-t from-[#0A192F]/60 via-transparent to-transparent opacity-70 transition-opacity duration-500 group-hover:opacity-90" />
                <span className="absolute left-4 bottom-4 text-white font-display text-sm font-medium tracking-wide translate-y-1 opacity-90 transition-transform duration-500 group-hover:translate-y-0">
                  {g.tag}
                </span>
              </button>
            </Reveal>
          ))}
        </div>
      </div>

      {/* Lightbox */}
      <AnimatePresence>
        {active !== null && (
          <motion.div
            data-testid="lightbox"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 z-[60] bg-[#0A192F]/95 backdrop-blur-sm flex items-center justify-center p-4 md:p-10"
            onClick={close}
          >
            <button
              onClick={close}
              data-testid="lightbox-close"
              className="absolute top-6 right-6 text-white/80 hover:text-white transition-colors"
              aria-label="Close"
            >
              <X size={30} />
            </button>
            <button
              onClick={(e) => { e.stopPropagation(); prev(); }}
              data-testid="lightbox-prev"
              className="absolute left-4 md:left-10 text-white/70 hover:text-[#1FB6C9] transition-colors"
              aria-label="Previous"
            >
              <ChevronLeft size={44} />
            </button>
            <motion.figure
              key={active}
              initial={{ opacity: 0, scale: 0.96 }}
              animate={{ opacity: 1, scale: 1 }}
              transition={{ duration: 0.4 }}
              className="max-w-5xl w-full"
              onClick={(e) => e.stopPropagation()}
            >
              <img
                src={GALLERY[active].src}
                alt={GALLERY[active].alt}
                className="w-full max-h-[78vh] object-contain rounded-lg"
              />
              <figcaption className="mt-4 flex items-center justify-between text-white/80 font-body text-sm">
                <span>{GALLERY[active].alt}</span>
                <span className="text-[#1FB6C9] uppercase tracking-widest">{GALLERY[active].tag}</span>
              </figcaption>
            </motion.figure>
            <button
              onClick={(e) => { e.stopPropagation(); next(); }}
              data-testid="lightbox-next"
              className="absolute right-4 md:right-10 text-white/70 hover:text-[#1FB6C9] transition-colors"
              aria-label="Next"
            >
              <ChevronRight size={44} />
            </button>
          </motion.div>
        )}
      </AnimatePresence>
    </section>
  );
}
